import { Base } from '@studiometa/js-toolkit';
import { toggleClass } from '@studiometa/js-toolkit/utils';
import { gsap } from 'gsap';

/**
 * Header component
 */
export default class Header extends Base {
  breakpoints = ['xxs', 'xs', 's', 'm'];

  /**
   * Component Configuration
   */
  static config = {
    name: 'Header',
    refs: ['nav', 'logo', 'navItems[]', 'socialItems[]', 'burger', 'wrapper'],
  };

  /** @type {boolean} */
  isHidden = false;

  /**
   *
   */
  mounted() {
    // Set initial hidden state for intro animation
    gsap.set(this.$refs.logo, { x: '-200%' });
    gsap.set(this.$refs.navItems, { opacity: 0, y: -20 });

    if (window.__pageTransitioning) {
      document.addEventListener('page:enter', () => this.intro(), { once: true });
    } else if (window.__loaderComplete) {
      this.intro();
    } else {
      document.addEventListener('loader:complete', () => this.intro(), { once: true });
    }
  }

  /**
   * Play the intro animation.
   */
  intro() {
    const tl = gsap.timeline();

    tl.to(this.$refs.logo, {
      x: 0,
      duration: 1,
      ease: 'power3.out',
    });

    tl.to(
      this.$refs.navItems,
      {
        opacity: 1,
        y: 0,
        duration: 0.6,
        ease: 'power3.out',
        stagger: 0.08,
      },
      '<0.2',
    );
  }

  /**
   *
   */
  onNavItemsClick() {
    if (this.isMobile) {
      this.toggleMenu();
    }
  }

  /**
   * Service scrolled hook
   * @param {object} props Props
   * @param {number} props.y The current vertical scroll position.
   * @param {object} props.direction Direction of scroll.
   */
  scrolled({ y, direction }) {
    const isOnTop = y === 0;

    if (isOnTop) {
      this.show();
      toggleClass(this.$refs.nav, 'text-black! bg-white', false);
      this.$refs.wrapper.classList.remove('bg-white', 'text-black!', 'shadow-md');
      return;
    }

    if (direction.y === 'DOWN' && !this.isHidden) {
      this.hide();
    } else if (direction.y === 'UP' && this.isHidden) {
      this.show();
    }
  }

  /** Hide header elements */
  hide() {
    this.isHidden = true;

    gsap.to(this.$refs.logo, {
      x: '-200%',
      duration: 0.5,
      ease: 'power3.in',
    });

    if (this.isMobile) {
      gsap.to(this.$refs.burger, {
        duration: 0.5,
        x: '200%',
        ease: 'power3.in',
      });
      this.$refs.wrapper.classList.remove('bg-white', 'text-black!', 'shadow-md');
    } else {
      toggleClass(this.$refs.nav, 'text-black! bg-white', false);
    }

    const items = /** @type {HTMLElement[]} */ ([...this.$refs.navItems]).reverse();
    items.forEach((item, i) => {
      const offsetRight = window.innerWidth - item.getBoundingClientRect().left;
      gsap.to(item, {
        x: offsetRight,
        duration: 0.4,
        ease: 'power3.in',
        delay: i * 0.05,
      });
    });
  }

  /** Show header elements */
  show() {
    this.isHidden = false;

    gsap.to(this.$refs.logo, {
      x: 0,
      duration: 0.5,
      ease: 'power3.out',
    });

    if (this.isMobile) {
      gsap.to(this.$refs.burger, {
        duration: 0.5,
        x: 0,
        ease: 'power3.out',
      });
      this.$refs.wrapper.classList.add('bg-white', 'text-black!', 'shadow-md');
    } else {
      toggleClass(this.$refs.nav, 'text-black! bg-white', true);
    }

    const items = /** @type {HTMLElement[]} */ ([...this.$refs.navItems]).reverse();
    items.forEach((item, i) => {
      gsap.to(item, {
        x: 0,
        duration: 0.4,
        ease: 'power3.out',
        delay: i * 0.05,
      });
    });
  }

  /**
   *
   */
  toggleMenu() {
    if (!this.mobileTl) {
      gsap.set(this.$refs.nav, { display: 'block', 'clip-path': 'inset(0% 0% 100% 0%)' });
      this.mobileTl = gsap
        .timeline({
          paused: true,
          onReverseComplete: () => {
            document.body.classList.remove('menu-is-open');
          },
          onStart: () => {
            document.body.classList.add('menu-is-open');
          },
        })
        .to(this.$refs.nav, {
          'clip-path': 'inset(0% 0% 0% 0%)',
          duration: 0.4,
          ease: 'power4.out',
        })
        .fromTo(
          this.$refs.navItems,
          {
            opacity: 0,
            y: -50,
          },
          {
            opacity: 1,
            y: 0,
            duration: 1,
            ease: 'power4.out',
            stagger: 0.08,
          },
          '-=0.2',
        )
        .fromTo(
          this.$refs.socialItems,
          {
            opacity: 0,
            y: 50,
          },
          {
            opacity: 1,
            y: 0,
            duration: 1,
            ease: 'power4.out',
            stagger: 0.08,
          },
          '<',
        );
    }

    const isOpening = this.mobileTl.reversed() || this.mobileTl.progress() === 0;

    if (isOpening) {
      this.mobileTl.play();
    } else {
      this.mobileTl.reverse();
    }
  }

  /**
   * Check if device is mobile
   * @returns {boolean}
   */
  get isMobile() {
    return this.breakpoints.includes(this.$services.get('resized').breakpoint);
  }
}
